import { Input, MinthrSelect, Sidebar, TextArea } from 'components/common';
import FileUpload from 'components/common/FileUpload';
import MintErrorMessage from 'components/common/MintErrorMessage';
import MinthrDateRangePicker from 'components/common/MinthrDateRangePicker';
import TruncateText from 'components/common/TruncateText';
import { useMediaContext } from 'contexts/MediaContext';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import {
  detailFormResponse,
  submitFormResponse,
} from 'features/forms/services/formServices';
import {
  DetailFormResponseFieldDTO,
  FieldType as FormFieldType,
  SubmitFormDTO,
} from 'features/forms/types';
import { parseParametresOptions } from 'features/forms/utils/parametres';
import { Form, Formik } from 'formik';
import moment from 'moment';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { checkFileType, getIconByExtension } from 'utils/globalMethodes';
import * as Yup from 'yup';

interface FormResponseValues {
  [key: string]: string | File[];
}

const EditResponse: FC<
  SideBarProps<{
    formId: number;
    responseId?: number;
    userId?: string;
    sharedId?: number;
    refetch?: () => void;
    withUser?: boolean;
  }>
> = ({ sidebar, setSidebarWidth, sidebarWidth, params }) => {
  const { t } = useTranslation();
  const { openMedia } = useMediaContext();
  const { closeSidebar } = useSidebarManager();
  const [form, setForm] = useState<SubmitFormDTO>();
  const [initialValue, setInitialValue] = useState<FormResponseValues | null>(
    null,
  );
  const [checkedValuesMap, setCheckedValuesMap] = useState<
    Record<string, string[]>
  >({});
  const [timeRangeValuesMap, setTimeRangeValuesMap] = useState<
    Record<string, string[]>
  >({});
  const [dateRangeValuesMap, setDateRangeValuesMap] = useState<
    Record<string, string[]>
  >({});
  const [error, setError] = useState<string>('');
  const emptyArray = React.useMemo(() => [], []);
  const [existingFiles, setExistingFiles] = useState<Record<string, string>>(
    {},
  );
  const validationErrorOccurredRef = useRef<Record<string, boolean>>({});

  const fetchForm = async (): Promise<void> => {
    try {
      const response = await detailFormResponse({
        formId: Number(params.formId),
        responseId: Number(params.responseId) || 0,
        sharedId: Number(params.sharedId) || 0,
      });
      if (response.success) {
        if (response.data) {
          setForm(response.data);
          const initialValues: FormResponseValues = {
            userId: response.data.userId || '',
          };
          const existingFilesMap: Record<string, string> = {};
          const checkedValuesMapTemp: Record<string, string[]> = {};
          const timeRangeValuesMapTemp: Record<string, string[]> = {};
          const dateRangeValuesMapTemp: Record<string, string[]> = {};
          response.data.fields?.forEach((field) => {
            const key = field.id.toString();
            if (field.fieldType === FormFieldType.File) {
              initialValues[key] = [];
              if (field.response) {
                existingFilesMap[key] = field.response;
              }
            } else if (
              field.fieldType === FormFieldType.DateRange &&
              field.response
            ) {
              const dates = field.response.split(',');
              if (dates.length === 2) {
                const start = moment(dates[0].trim(), [
                  'YYYY-MM-DD',
                  'DD/MM/YYYY',
                ]);
                const end = moment(dates[1].trim(), [
                  'YYYY-MM-DD',
                  'DD/MM/YYYY',
                ]);
                if (start.isValid() && end.isValid()) {
                  initialValues[`${key}_start`] = start.format('DD/MM/YYYY');
                  initialValues[`${key}_end`] = end.format('DD/MM/YYYY');
                  dateRangeValuesMapTemp[key] = [
                    start.format('DD/MM/YYYY'),
                    end.format('DD/MM/YYYY'),
                  ];
                } else {
                  initialValues[`${key}_start`] = '';
                  initialValues[`${key}_end`] = '';
                  dateRangeValuesMapTemp[key] = ['', ''];
                }
              } else {
                const parsedDate = moment(field.response, [
                  'YYYY-MM-DD',
                  'DD/MM/YYYY',
                ]);
                if (parsedDate.isValid()) {
                  initialValues[`${key}_start`] =
                    parsedDate.format('DD/MM/YYYY');
                  initialValues[`${key}_end`] = parsedDate.format('DD/MM/YYYY');
                  dateRangeValuesMapTemp[key] = [
                    parsedDate.format('DD/MM/YYYY'),
                    parsedDate.format('DD/MM/YYYY'),
                  ];
                } else {
                  initialValues[`${key}_start`] = '';
                  initialValues[`${key}_end`] = '';
                  dateRangeValuesMapTemp[key] = ['', ''];
                }
              }
            } else if (
              field.fieldType === FormFieldType.Date &&
              field.response
            ) {
              const parsedDate = moment(field.response, [
                'YYYY-MM-DD',
                'DD/MM/YYYY',
              ]);
              initialValues[key] = parsedDate.isValid()
                ? parsedDate.format('DD/MM/YYYY')
                : field.response;
            } else {
              initialValues[key] = field.response ?? '';
              if (
                field.fieldType === FormFieldType.Checkbox &&
                field.response
              ) {
                // Response may be a JSON array string (new) or comma-separated (legacy).
                const parsed = parseParametresOptions(field.response);
                checkedValuesMapTemp[key] = parsed;
                initialValues[key] = parsed.join(',');
              }
              if (
                field.fieldType === FormFieldType.TimeRange &&
                field.response
              ) {
                timeRangeValuesMapTemp[key] = field.response.split(',');
              }
            }
          });
          setExistingFiles(existingFilesMap);
          setCheckedValuesMap(checkedValuesMapTemp);
          setTimeRangeValuesMap(timeRangeValuesMapTemp);
          setDateRangeValuesMap(dateRangeValuesMapTemp);
          setInitialValue(initialValues);
        } else {
          toast.error(t('Error.Status.404'));
          closeSidebar(EditResponse);
        }
      }
    } catch (error) {
      console.error('Error fetching form details:', error);
      closeSidebar(EditResponse);
    }
  };
  useEffect(() => {
    fetchForm();
  }, [params.formId]);

  function generateValidationSchema(
    fields: Array<{ fieldType: number; isRequired?: boolean; id: number }>, // Added id for keying
  ) {
    const shape: Record<string, Yup.AnySchema> = {};
    if (
      !params.withUser &&
      Number(params.responseId) === 0 &&
      !form?.isAnonymous &&
      form?.isAuthorized
    ) {
      shape.userId = Yup.string().required(t('RequiredAttribute'));
    }
    fields?.forEach((field) => {
      const key = field.id.toString();
      switch (field.fieldType) {
        case FormFieldType.Number: {
          const numberSchema = Yup.number().transform(
            (value, originalValue) => {
              if (typeof originalValue === 'string') {
                const v = originalValue.replace(',', '.').trim();
                return v === '' ? NaN : Number(v);
              }
              return value;
            },
          );
          shape[key] = field.isRequired
            ? numberSchema
                .typeError(t('validation.number'))
                .required(t('RequiredAttribute'))
            : numberSchema.typeError(t('validation.number')).nullable();
          break;
        }
        case FormFieldType.Text:
          shape[key] = field.isRequired
            ? Yup.string().trim().required(t('RequiredAttribute'))
            : Yup.string();
          break;
        case FormFieldType.Email:
          shape[key] = field.isRequired
            ? Yup.string()
                .email(t('Invalid.Email'))
                .required(t('RequiredAttribute'))
            : Yup.string().email(t('Invalid.Email'));
          break;
        case FormFieldType.Password:
          shape[key] = field.isRequired
            ? Yup.string().required(t('RequiredAttribute'))
            : Yup.string();
          break;
        case FormFieldType.Date:
          shape[key] = field.isRequired
            ? Yup.string().required(t('RequiredAttribute'))
            : Yup.string();
          break;
        case FormFieldType.Time:
          shape[key] = field.isRequired
            ? Yup.string().required(t('RequiredAttribute'))
            : Yup.string();
          break;
        case FormFieldType.Checkbox:
        case FormFieldType.Radio:
          shape[key] = field.isRequired
            ? Yup.string().required(t('RequiredAttribute'))
            : Yup.string();
          break;
        case FormFieldType.Select:
          shape[key] = field.isRequired
            ? Yup.string().required(t('RequiredAttribute'))
            : Yup.string();
          break;
        case FormFieldType.TextArea:
          shape[key] = field.isRequired
            ? Yup.string().trim().required(t('RequiredAttribute'))
            : Yup.string();
          break;
        case FormFieldType.File:
          shape[key] = field.isRequired
            ? Yup.array().min(1, t('RequiredAttribute'))
            : Yup.array();
          break;
        case FormFieldType.Url:
          shape[key] = field.isRequired
            ? Yup.string()
                .url(t('RequiredAttribute'))
                .required(t('RequiredAttribute'))
            : Yup.string().url(t('RequiredAttribute'));
          break;
        case FormFieldType.Tel:
          shape[key] = field.isRequired
            ? Yup.string().required(t('RequiredAttribute'))
            : Yup.string();
          break;
        case FormFieldType.DateTime:
          shape[key] = field.isRequired
            ? Yup.date()
                .typeError(t('Form.InvalidType'))
                .required(t('RequiredAttribute'))
            : Yup.date().typeError(t('Form.InvalidType')).nullable();
          break;
        case FormFieldType.Color:
          shape[key] = field.isRequired
            ? Yup.string().required(t('RequiredAttribute'))
            : Yup.string();
          break;
        case FormFieldType.DateRange: {
          const startKey = `${key}_start`;
          const endKey = `${key}_end`;
          const base = Yup.string();
          shape[startKey] = field.isRequired
            ? base.required(t('RequiredAttribute'))
            : base;
          shape[endKey] = field.isRequired
            ? base.required(t('RequiredAttribute'))
            : base;
          break;
        }
        case FormFieldType.TimeRange:
          shape[key] = field.isRequired
            ? Yup.string().required(t('RequiredAttribute'))
            : Yup.string();
          break;
      }
    });

    return Yup.object().shape(shape);
  }
  const handleDownload = (filePath: string, fileName: string) => {
    openMedia(`/upload/VueFileByName/${filePath}`, 'pdf', fileName);
  };

  const directDownload = (filePath: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = `/upload/VueFileByName/${filePath}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const appendBasicFormData = (
    formData: FormData,
    values: FormResponseValues,
  ) => {
    formData.append('FormId', params.formId.toString());
    if (Number(params.responseId) > 0) {
      formData.append('ResponseId', params.responseId!.toString());
    }
    const userId = values.userId as string;
    if ((params.withUser || form?.isAuthorized) && userId) {
      formData.append('UserId', userId);
    }
  };

  const appendFieldProperties = (
    formData: FormData,
    field: DetailFormResponseFieldDTO,
    index: number,
  ) => {
    formData.append(`Fields[${index}].Id`, field.id?.toString() || '');

    if (field.fieldType?.toString() == '19') {
      formData.append(`Fields[${index}].Parametres`, field.parametres ?? '');
      formData.append(
        `Fields[${index}].valuesPath`,
        field.valuesPath?.toString() || '',
      );
    }
  };

  const getFieldResponse = (
    field: DetailFormResponseFieldDTO,
    values: FormResponseValues,
  ): string => {
    const key = field.id.toString();
    const responseHandlers = {
      // Number accepts both ',' and '.' as decimal separators; normalize to '.'.
      [FormFieldType.Number]: (): string => {
        const value = values[key] as string;
        return value ? value.replace(',', '.') : '';
      },
      // Multi-select checkbox is sent as a JSON array string, e.g. ["Avion","Tain"].
      [FormFieldType.Checkbox]: () => {
        const selected = checkedValuesMap[key];
        return selected && selected.length > 0 ? JSON.stringify(selected) : '';
      },
      [FormFieldType.TimeRange]: () => timeRangeValuesMap[key]?.join(',') || '',
      // Date range is sent as `yyyy-MM-dd,yyyy-MM-dd`.
      [FormFieldType.DateRange]: () => {
        const range = dateRangeValuesMap[key];
        if (!range || range.length === 0) return '';
        return range
          .map((d) => (d ? moment(d, 'DD/MM/YYYY').format('YYYY-MM-DD') : ''))
          .join(',');
      },
      // Single date is sent as `yyyy-MM-dd`.
      [FormFieldType.Date]: () => {
        const value = values[key] as string;
        if (!value) return '';
        const parsed = moment(value, 'DD/MM/YYYY');
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
      },
      [FormFieldType.File]: () => {
        const files = values[key] as File[];
        if (files && files.length > 0) {
          return files.map((file) => file.name).join(',');
        }
        return existingFiles[key] || '';
      },
    };

    const handler =
      responseHandlers[field.fieldType as keyof typeof responseHandlers];
    return handler ? handler() : (values[key] as string) || '';
  };

  const handleFileUpload = (
    formData: FormData,
    field: DetailFormResponseFieldDTO,
    index: number,
    values: FormResponseValues,
  ) => {
    if (field.fieldType === FormFieldType.File) {
      const key = field.id.toString();
      const files = values[key] as File[];
      if (files && files.length > 0) {
        files.forEach((file) => {
          formData.append(`Fields[${index}].Files`, file);
        });
      }
    }
  };

  const onSubmit = async (values: FormResponseValues) => {
    if (params.withUser) {
      values.userId = params.userId ?? '';
    }

    const formData = new FormData();

    appendBasicFormData(formData, values);

    form?.fields?.forEach((field, index) => {
      appendFieldProperties(formData, field, index);
      handleFileUpload(formData, field, index, values);
      const response = getFieldResponse(field, values);
      formData.append(`Fields[${index}].Response`, response);
    });

    const response = await submitFormResponse(formData);
    if (response.success) {
      toast.success(t('Default.succesMessage'));
      params?.refetch?.();
      closeSidebar(EditResponse);
    } else {
      setError(response.message);
    }
  };

  if (!initialValue || !form) {
    return null;
  }

  return (
    <Sidebar
      size="m"
      type="primary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
      id="responseSidebar"
    >
      <>
        <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-4 minthr-border-b minthr-border-b-3">
          <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
            <div className="minthr-icon-m">
              <i className={`fa-solid ${form.iconPath}`}></i>
            </div>
          </div>
          <h3>{form?.title}</h3>

          <button
            className="minthr-sidebar-close"
            type="button"
            onClick={() => closeSidebar(EditResponse)}
          >
            <img src="/icons/close.svg" alt="" />
          </button>
        </div>
        <Formik
          initialValues={initialValue}
          onSubmit={onSubmit}
          validationSchema={generateValidationSchema(form.fields ?? [])}
        >
          {({
            isSubmitting,
            setFieldValue,
            values,
            errors,
            touched,
            handleBlur,
          }) => {
            Object.keys(errors).forEach((key) => {
              if (touched[key]) {
                validationErrorOccurredRef.current[key] = true;
              }
            });
            return (
              <Form className="w-100">
                {error && <MintErrorMessage message={error} />}
                {!params.withUser &&
                  Number(params.responseId) === 0 &&
                  !form.isAnonymous &&
                  form.isAuthorized && (
                    <div className="g-0 minthr-mb-5 minthr-mobile-mb-0">
                      <MinthrSelect
                        name="userId"
                        label={t('Form.Response.Create.f.users')}
                        options={
                          form.usersList?.map((user) => ({
                            label: user.text,
                            value: user.value,
                          })) || []
                        }
                        onchange={(user: { value: string; text: string }) => {
                          setFieldValue('userId', user.value);
                        }}
                      />
                    </div>
                  )}

                {form?.isAnonymous && (
                  <div className="minthr-bg-warning-opacity minthr-color-warning minthr-py-1 minthr-px-2 minthr-border-rounded-right minthr-border-rounded-bottom minthr-mb-5">
                    <img
                      className="minthr-icon-warning minthr-icon-s minthr-mr-1"
                      src="/icons/lock.svg"
                      alt=""
                    />
                    {t('Form.Anonymous.Message')}
                  </div>
                )}
                {form?.fields &&
                  form?.fields.map((field) => (
                    <>
                      {(field.fieldType == FormFieldType.Text ||
                        field.fieldType == FormFieldType.Email ||
                        field.fieldType == FormFieldType.Number) && (
                        <div
                          className={`row g-0  minthr-mobile-mb-0 ${field.description != null && field.description != '' ? ' minthr-mb-1' : ' minthr-mb-5'}`}
                        >
                          <div className="col-sm-12 minthr-mobile-mb-2">
                            <div className="minthr-form-outline FieldInfo">
                              <Input
                                type={field.fieldType.toString()}
                                name={field.id.toString()}
                                label={field.title ?? ''}
                                numericOnly={
                                  field.fieldType == FormFieldType.Number
                                }
                                hasDoubleValue={
                                  field.fieldType == FormFieldType.Number
                                }
                              />
                            </div>
                          </div>
                          {field.description != null &&
                            field.description != '' && (
                              <span className="p">{field.description}</span>
                            )}
                        </div>
                      )}
                      {field.fieldType == FormFieldType.Description && (
                        <div className="minthr-bg-white minthr-p-1 minthr-w-100 minthr-border-rounded minthr-mb-4 minthr-word-wrap">
                          <span className="p ">{field.title}</span>
                        </div>
                      )}
                      {field.fieldType == FormFieldType.Time && (
                        <div
                          className={`row g-0  minthr-mobile-mb-0 ${field.description != null && field.description != '' ? ' minthr-mb-1' : ' minthr-mb-5'}`}
                        >
                          <div className="col-sm-12 minthr-mobile-mb-2">
                            <div
                              className={`minthr-form-outline FieldInfo  ${errors[field.id.toString()] && touched[field.id.toString()] ? 'error' : ''}`}
                            >
                              <input
                                className="minthr-form-control active"
                                type="time"
                                name={field.id.toString()}
                                value={values[field.id.toString()] as string}
                                onChange={(e) => {
                                  setFieldValue(
                                    field.id.toString(),
                                    e.target.value,
                                  );
                                }}
                                onBlur={handleBlur}
                                id={`field_${field.id}`}
                              />
                              <label
                                className="minthr-form-label"
                                htmlFor={`field_${field.id}`}
                              >
                                {field.title}
                              </label>
                              {touched[field.id.toString()] &&
                              errors[field.id.toString()] ? (
                                <div className="minthr-form-message">
                                  <span className="field-validation-error">
                                    <span>{errors[field.id.toString()]}</span>
                                  </span>
                                </div>
                              ) : null}
                              <div className="minthr-form-notch">
                                <div className="minthr-form-notch-leading"></div>
                                <div
                                  className="minthr-form-notch-middle"
                                  style={{ minWidth: '49.0781px' }}
                                ></div>
                                <div className="minthr-form-notch-trailing"></div>
                              </div>
                            </div>
                          </div>
                          {field.description != null &&
                            field.description != '' && (
                              <span className="p">{field.description}</span>
                            )}
                        </div>
                      )}

                      {field.fieldType == FormFieldType.Date && (
                        <div
                          className={`row g-0  minthr-mobile-mb-0 ${field.description != null && field.description != '' ? ' minthr-mb-1' : ' minthr-mb-5'}`}
                        >
                          <div className="col-sm-12 minthr-mobile-mb-2">
                            <div className="minthr-form-outline FieldInfo">
                              <MinthrDateRangePicker
                                label={field.title ?? ''}
                                startDateName={field.id.toString()}
                                singleDatePicker
                                saveFormat="DD/MM/YYYY"
                              />
                            </div>
                          </div>
                          {field.description != null &&
                            field.description != '' && (
                              <span className="p">{field.description}</span>
                            )}
                        </div>
                      )}
                      {field.fieldType == FormFieldType.DateRange && (
                        <div className="row g-0  minthr-mobile-mb-0  minthr-mb-5">
                          <div className="col-sm-12 minthr-mobile-mb-2">
                            {!field.hideTitle && (
                              <span className="p minthr-color-dark">
                                {field.title}
                              </span>
                            )}
                            <div className="FieldInfo minthr-mt-1">
                              <MinthrDateRangePicker
                                startDateName={`${field.id}_start`}
                                endDateName={`${field.id}_end`}
                                onChange={(startDate, endDate) => {
                                  const key = field.id.toString();
                                  const startStr = startDate
                                    ? startDate.format('DD/MM/YYYY')
                                    : '';
                                  const endStr = endDate
                                    ? endDate.format('DD/MM/YYYY')
                                    : '';
                                  setDateRangeValuesMap((prev) => ({
                                    ...prev,
                                    [key]: [startStr, endStr],
                                  }));
                                  setFieldValue(`${key}_start`, startStr);
                                  setFieldValue(`${key}_end`, endStr);
                                }}
                                customView={(startDate, endDate) => {
                                  const startKey = `${field.id}_start`;
                                  const endKey = `${field.id}_end`;
                                  const hasStartError = errors[startKey];
                                  const hasEndError = errors[endKey];
                                  const hasErrors =
                                    hasStartError || hasEndError;

                                  return startDate && endDate ? (
                                    <div className="w-100 minthr-border-rounded minthr-border minthr-color-primary minthr-text-align-center minthr-p-3 dateRange">
                                      <span>
                                        {startDate?.format('DD/MM/YYYY')}
                                      </span>
                                      -
                                      <span>
                                        {endDate?.format('DD/MM/YYYY')}
                                      </span>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="w-100 minthr-border-rounded minthr-border minthr-color-primary minthr-text-align-center minthr-p-3 dateRange">
                                        <span>{t('Holiday.SelectPeriod')}</span>
                                      </div>
                                      {hasErrors ? (
                                        <div
                                          className="minthr-form-message minthr-flex"
                                          style={{
                                            color:
                                              'var(--minthr-color-danger) !important',
                                            fontSize:
                                              'calc(var(--unit-minthr) * 11)!important',
                                          }}
                                        >
                                          <span>
                                            {hasStartError || hasEndError}
                                          </span>
                                        </div>
                                      ) : null}
                                    </>
                                  );
                                }}
                                saveFormat="DD/MM/YYYY"
                              />
                            </div>
                          </div>
                          {field.description != null &&
                            field.description != '' && (
                              <span className="p">{field.description}</span>
                            )}
                        </div>
                      )}
                      {field.fieldType == FormFieldType.TimeRange && (
                        <div
                          className={`row g-0  minthr-mobile-mb-0 ${field.description != null && field.description != '' ? ' minthr-mb-1' : ' minthr-mb-5'}`}
                        >
                          <div
                            className="col-sm-12 minthr-mobile-mb-2 timeRange"
                            data-fieldid={field.id}
                          >
                            {!field.hideTitle && (
                              <span className="p minthr-color-dark">
                                {field.title}
                              </span>
                            )}
                            <div className="minthr-flex minthr-flex-items-center FieldInfo minthr-mt-2">
                              <div className="col-sm-6 minthr-pr-2">
                                <div
                                  className={`minthr-form-outline ${errors[field.id.toString()] && touched[field.id.toString()] ? 'error' : ''}`}
                                >
                                  <input
                                    className="minthr-form-control active minthr-border"
                                    id={`StartTime_${field.id}`}
                                    name={field.id.toString()}
                                    type="time"
                                    value={
                                      timeRangeValuesMap[
                                        field.id.toString()
                                      ]?.[0] || ''
                                    }
                                    onChange={(e) => {
                                      const key = field.id.toString();
                                      const newValues = [
                                        ...(timeRangeValuesMap[key] || [
                                          '',
                                          '',
                                        ]),
                                      ];
                                      newValues[0] = e.target.value;
                                      setTimeRangeValuesMap((prev) => ({
                                        ...prev,
                                        [key]: newValues,
                                      }));
                                      setFieldValue(key, newValues.join(','));
                                    }}
                                    onBlur={handleBlur}
                                  />
                                  <label
                                    className="minthr-form-label"
                                    htmlFor={`StartTime_${field.id}`}
                                  >
                                    {t('Form.start')}
                                  </label>
                                  {touched[field.id.toString()] &&
                                  errors[field.id.toString()] ? (
                                    <div className="minthr-form-message">
                                      <span className="field-validation-error">
                                        <span>
                                          {errors[field.id.toString()]}
                                        </span>
                                      </span>
                                    </div>
                                  ) : null}
                                  <div className="minthr-form-notch">
                                    <div className="minthr-form-notch-leading"></div>
                                    <div
                                      className="minthr-form-notch-middle"
                                      style={{ minWidth: '49.0781px' }}
                                    ></div>
                                    <div className="minthr-form-notch-trailing"></div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6">
                                <div
                                  className={`minthr-form-outline ${errors[field.id.toString()] && touched[field.id.toString()] ? 'error' : ''}`}
                                >
                                  <input
                                    className="minthr-form-control active minthr-border"
                                    id={`EndTime_${field.id}`}
                                    name={field.id.toString()}
                                    type="time"
                                    value={
                                      timeRangeValuesMap[
                                        field.id.toString()
                                      ]?.[1] || ''
                                    }
                                    onChange={(e) => {
                                      const key = field.id.toString();
                                      const newValues = [
                                        ...(timeRangeValuesMap[key] || [
                                          '',
                                          '',
                                        ]),
                                      ];
                                      newValues[1] = e.target.value;
                                      setTimeRangeValuesMap((prev) => ({
                                        ...prev,
                                        [key]: newValues,
                                      }));
                                      setFieldValue(key, newValues.join(','));
                                    }}
                                    onBlur={handleBlur}
                                  />
                                  <label
                                    className="minthr-form-label"
                                    htmlFor={`EndTime_${field.id}`}
                                  >
                                    {t('Form.end')}
                                  </label>
                                  <div className="minthr-form-notch">
                                    <div className="minthr-form-notch-leading"></div>
                                    <div
                                      className="minthr-form-notch-middle"
                                      style={{ minWidth: '29.0781px' }}
                                    ></div>
                                    <div className="minthr-form-notch-trailing"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {field.description != null &&
                            field.description != '' && (
                              <span className="p">{field.description}</span>
                            )}
                        </div>
                      )}
                      {(field.fieldType == FormFieldType.Checkbox ||
                        field.fieldType == FormFieldType.Radio) && (
                        <div className="minthr-bg-white  minthr-w-100 minthr-border-rounded  minthr-mb-4">
                          <span className="p minthr-color-dark">
                            {field.title}
                          </span>
                          <div className="minthr-flex minthr-flex-items-center minthr-mt-1 minthr-flex-wrap FieldInfo">
                            {field.parametres &&
                              parseParametresOptions(field.parametres).map(
                                (item) => (
                                  <div
                                    key={item}
                                    data-fieldtype={field.fieldType}
                                    data-fieldid={field.id}
                                    className={`minthr-chackbox minthr-mr-3  CheckBoxValFieldTypeue ${field.fieldType == FormFieldType.Radio ? 'minthr-chackbox-circle' : ''}`}
                                  >
                                    <input
                                      type={
                                        field.fieldType == FormFieldType.Radio
                                          ? 'radio'
                                          : 'checkbox'
                                      }
                                      name={field.id.toString()}
                                      id={`field_${field.id}-${item}`}
                                      data-nameField="Response_@i"
                                      onChange={(e) => {
                                        const key = field.id.toString();
                                        if (
                                          field.fieldType ===
                                          FormFieldType.Radio
                                        ) {
                                          setFieldValue(key, item);
                                        } else {
                                          let newChecked =
                                            checkedValuesMap[key] || [];
                                          if (
                                            e.target.checked &&
                                            !newChecked.includes(item)
                                          ) {
                                            newChecked = [...newChecked, item];
                                          } else {
                                            newChecked = newChecked.filter(
                                              (v) => v !== item,
                                            );
                                          }
                                          setCheckedValuesMap((prev) => ({
                                            ...prev,
                                            [key]: newChecked,
                                          }));
                                          setFieldValue(
                                            key,
                                            newChecked.join(','),
                                          );
                                        }
                                      }}
                                      value={item}
                                      checked={
                                        field.fieldType === FormFieldType.Radio
                                          ? values[field.id.toString()] === item
                                          : checkedValuesMap[
                                              field.id.toString()
                                            ]?.includes(item) || false
                                      }
                                      className={`minthr-form-control checkbox-response ${field.response != null && field.response.includes(item) ? 'checked' : ''}`}
                                    />
                                    <label
                                      className="minthr-form-label"
                                      htmlFor={`field_${field.id}-${item}`}
                                    >
                                      {item}
                                    </label>
                                  </div>
                                ),
                              )}
                          </div>
                          {errors[field.id.toString()] && (
                            <div className="text-s minthr-color-danger">
                              {errors[field.id.toString()]}
                            </div>
                          )}
                          {field.description != null &&
                            field.description != '' && (
                              <span className="p">{field.description}</span>
                            )}
                        </div>
                      )}
                      {field.fieldType == FormFieldType.Select &&
                        field.parametres && (
                          <div
                            className={`row g-0  minthr-mobile-mb-0 ${field.description != null && field.description != '' ? ' minthr-mb-1' : ' minthr-mb-5'}`}
                          >
                            <div className="col-sm-12 FieldInfo">
                              <MinthrSelect
                                name={field.id.toString()}
                                label={field.title ?? ''}
                                options={
                                  parseParametresOptions(field.parametres).map(
                                    (item) => {
                                      const parts = item.trim().split(' - ');
                                      if (parts.length === 2) {
                                        return {
                                          label: parts[1].trim(),
                                          value: parts[0].trim(),
                                        };
                                      } else {
                                        return {
                                          label: item.trim(),
                                          value: item.trim(),
                                        };
                                      }
                                    },
                                  ) || []
                                }
                              />
                            </div>
                            {field.description != null &&
                              field.description != '' && (
                                <span className="p">{field.description}</span>
                              )}
                          </div>
                        )}
                      {field.fieldType == FormFieldType.TextArea && (
                        <div
                          className={`row g-0  minthr-mobile-mb-0 ${field.description != null && field.description != '' ? ' minthr-mb-1' : ' minthr-mb-5'}`}
                        >
                          <div className="col-sm-12 minthr-mobile-mb-2">
                            <TextArea
                              className=""
                              label={field.title ?? ''}
                              name={field.id.toString()}
                            />
                          </div>
                          {field.description != null &&
                            field.description != '' && (
                              <span className="p">{field.description}</span>
                            )}
                        </div>
                      )}
                      {field.fieldType == FormFieldType.File && (
                        <>
                          {(() => {
                            const fieldId = field.id.toString();
                            const hasValidationError =
                              validationErrorOccurredRef.current[fieldId];
                            const hasCurrentError =
                              errors[fieldId] &&
                              (touched[fieldId] === true ||
                                (Array.isArray(touched[fieldId]) &&
                                  touched[fieldId].length === 0));
                            const hasUploadedFiles =
                              (values[fieldId] as File[])?.length > 0;
                            const hasExistingFiles =
                              field.filesNames && field.filesNames.length > 0;

                            // Hide old files if validation error occurred (past or present) and user hasn't uploaded files yet
                            // OR if validation error occurred and user uploaded files (keep hidden even after error clears)
                            const shouldHideOldFiles =
                              hasValidationError &&
                              (hasCurrentError || hasUploadedFiles);
                            const shouldShowOldFiles =
                              hasExistingFiles && !shouldHideOldFiles;

                            return (
                              <>
                                {shouldShowOldFiles && (
                                  <>
                                    <span className="p minthr-color-dark">
                                      {field.title}
                                    </span>
                                    <div className="minthr-w-100 minthr-mt-2 minthr-mb-1">
                                      {field.filesNames?.map((file) => (
                                        <a
                                          key={file.id}
                                          className="minthr-flex minthr-flex-items-center minthr-bg-light-gray minthr-text-decoration-none minthr-p-2 minthr-border-rounded minthr-mb-2"
                                        >
                                          <img
                                            className="minthr-icon-xl minthr-mr-2"
                                            src={`/icons/${getIconByExtension(file.path)}`}
                                            alt="File icon"
                                          />
                                          <div className="minthr-mr-6 minthr-pr-3 minthr-text-truncate">
                                            <TruncateText tag="h6" showTooltip>
                                              {file.name}
                                            </TruncateText>
                                          </div>
                                          <button
                                            type="button"
                                            className={`minthr-p-0 minthr-border-none minthr-bg-transparent minthr-ml-auto minthr-mr-2 ${checkFileType(file.path) === 'pdf' ? 'pdf-vue' : checkFileType(file.path) === 'image' ? 'img-vue' : ''}`}
                                            onClick={() => {
                                              if (
                                                checkFileType(file.path) ===
                                                  'pdf' ||
                                                checkFileType(file.path) ===
                                                  'image'
                                              ) {
                                                handleDownload(
                                                  file.path,
                                                  file.name,
                                                );
                                              } else {
                                                directDownload(
                                                  file.path,
                                                  file.name,
                                                );
                                              }
                                            }}
                                          >
                                            <img
                                              className="minthr-icon-s minthr-icon-gray"
                                              src={`/icons/${checkFileType(file.path) === false ? 'download' : 'eye'}.svg`}
                                              alt=""
                                            />
                                          </button>
                                        </a>
                                      ))}
                                    </div>
                                  </>
                                )}
                                <FileUpload
                                  name={field.id.toString()}
                                  allowedFileTypes={
                                    field.parametres == 'image'
                                      ? [
                                          'png',
                                          'jpg',
                                          'jpeg',
                                          'gif',
                                          'bmp',
                                          'svg',
                                        ]
                                      : field.parametres == 'pdf'
                                        ? ['pdf']
                                        : '*'
                                  }
                                  initialFiles={emptyArray}
                                  inputContainerClassName={`col-sm-12 ${field.description != null && field.description != '' ? ' minthr-mb-1' : ' minthr-mb-5'}`}
                                  labelClass="minthr-files minthr-border-rounded minthr-bg-primary-opacity minthr-p-6 w-100"
                                  imageClass="minthr-icon-xl minthr-mb-3"
                                  filesContainerClassName="minthr-sidebar-content-files new"
                                  contentClassName=""
                                  labelText={
                                    field.filesNames &&
                                    field.filesNames.length > 0 &&
                                    !shouldHideOldFiles
                                      ? t('Form.AddMoreFiles')
                                      : field.title || ''
                                  }
                                  customizeUpload
                                  errorsInside
                                />
                                {field.description != null &&
                                  field.description != '' && (
                                    <span className="p">
                                      {field.description}
                                    </span>
                                  )}
                              </>
                            );
                          })()}
                        </>
                      )}
                      {field.fieldType == FormFieldType.InputFile &&
                        field.filesNames != null && (
                          <>
                            {field.filesNames.length > 0 && (
                              <>
                                <span className="p minthr-color-dark">
                                  {field.title}
                                </span>

                                <div className="minthr-w-100 ">
                                  {field.filesNames.map((file) => (
                                    <a
                                      key={file.id}
                                      className="minthr-flex minthr-flex-items-center  minthr-bg-light-gray minthr-text-decoration-none minthr-p-2 minthr-border-rounded minthr-mb-3"
                                    >
                                      <img
                                        className="minthr-icon-xl minthr-mr-2"
                                        src="/icons/doc-pdf.svg"
                                        alt=""
                                      />
                                      <div className=" minthr-pr-3 minthr-w-60">
                                        <h6 className="minthr-text-truncate w-100">
                                          {file.name}
                                        </h6>
                                      </div>

                                      <button
                                        type="button"
                                        className="minthr-p-0 minthr-border-none minthr-bg-transparent minthr-ml-auto minthr-mr-2 pdf-vue"
                                        onClick={() => {
                                          handleDownload(file.path, file.name);
                                        }}
                                      >
                                        <img
                                          className="minthr-icon-s minthr-icon-gray"
                                          src="/icons/eye.svg"
                                          alt=""
                                        />
                                      </button>
                                    </a>
                                  ))}
                                </div>
                              </>
                            )}
                          </>
                        )}
                    </>
                  ))}

                <div className="minthr-sidebar-footer minthr-box-shadow minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-py-4 minthr-mx-n5 minthr-px-4 minthr-mt-auto">
                  <button
                    type="submit"
                    className="minthr-btn minthr-btn-primary minthr-ml-auto"
                    disabled={isSubmitting}
                  >
                    {t('btn.save')}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </>
    </Sidebar>
  );
};

export default EditResponse;
